import {
  ArrowDownLeftIcon,
  ArrowLeftIcon,
  ArrowUpRightIcon,
  ListPlusIcon,
  PlusIcon,
  SaveIcon,
} from "lucide-react"
import type * as React from "react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { CHALLAN_TYPES, challanDirection, type ChallanType } from "@steelbill/types"
import { ChosenCustomer, CustomerPicker, ItemPicker } from "@/features/invoices/DocumentPickers"
import { LineCard, TotalsBar } from "@/features/invoices/InvoiceEditorScreen"
import { DraftConflictAlert } from "@/features/drafts/DraftConflictAlert"
import { DraftNameBadge } from "@/features/drafts/DraftNameBadge"
import { DraftReadyControl } from "@/features/drafts/DraftReadyControl"
import { useDraft, useDraftAutosave } from "@/features/drafts/use-document-draft"
import {
  adoptedLines,
  emptyLine,
  linesFromInvoice,
  makeKeyFactory,
  useCustomerById,
  type EditorLine,
} from "@/features/invoices/use-invoice-editor"
import { useChallan } from "@/features/challans/use-challan"
import {
  emptyChallanState,
  stateFromChallan,
  toChallanApiBody,
  useChallanPreview,
  useSaveChallan,
  withChallanType,
  withIsReturnable,
  type ChallanEditorState,
} from "@/features/challans/use-challan-editor"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { DateField } from "@/components/DateField"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { requestBlob } from "@/lib/api"
import { ApiError } from "@/lib/api-error"
import { todayInputValue } from "@/lib/dates"
import { endpoints, type Customer } from "@/lib/endpoints"
import { basisPointsToInputValue } from "@/lib/units"
import { routes } from "@/routes/paths"

/**
 * Writing a delivery challan (§17.7, Rule 55 CGST).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ONE SCREEN FOR "NEW" AND "EDIT", exactly as the quotation editor is, and for
 * the same reason: the lines, the preview and §9.1's group rules are identical,
 * and two editors is how one of them ends up missing a field. `:id` in the route
 * is what tells them apart.
 *
 * THERE IS NO "MAKE A COPY", AND THAT IS THE ONE STRUCTURAL DIFFERENCE FROM THE
 * QUOTATION EDITOR. `routes/paths.ts` declares no `challanCopy` on purpose:
 * every movement is its own event, with its own date, its own lorry and its own
 * reason. A copied challan would carry last week's vehicle number to today's
 * gate — and the vehicle number is the one field on the page the person checking
 * it can actually verify. So `?copyFrom=` is not read here, and
 * `stateFromChallan` takes no `{ copy, today }` option bag.
 *
 * A CHALLAN IS NOT A TAX INVOICE. It carries a taxable value because Rule 55(1)
 * requires one; none of it is output tax, nothing here reaches GSTR-3B, and only
 * the serial range is declared, in GSTR-1 (§17.4). The lines card says so in
 * words, because the shared totals bar below shows a rupee figure and an
 * unexplained rupee figure on a document reads as money owed.
 *
 * EVERY FIGURE COMES FROM THE SERVER, via `POST /challans/preview` (§6,
 * CLAUDE.md). Nothing here multiplies a rate by a quantity.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/* See `makeKeyFactory` — `adopt` on the resume path is what stops a restored
 * draft's keys colliding with newly added lines. "C" for challan; "Q" and "L"
 * are taken by the quotation and invoice editors, and although `adopt` reads
 * only the trailing number, a distinct prefix is what makes a stray key in a
 * draft payload traceable to the screen that wrote it. */
const lineKeys = makeKeyFactory("C")
const nextKey = lineKeys.next

/**
 * WHY THE GOODS ARE MOVING, IN THE OWNER'S WORDS.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * NEVER THE ENUM. `JOB_WORK_IN` is a database value; "Customer's material
 * received for job work" is the sentence that tells the person filling the form
 * which of the two job-work rows he is on — and choosing the wrong one inverts
 * the consigner and consignee blocks on paper that travels with the goods.
 *
 * KEYED BY `ChallanType`, so adding a type to the enum fails the build here
 * rather than rendering a blank option that saves a movement with no stated
 * reason. Rule 55(1) requires the reason ON the document; a silent gap in this
 * table would be a legal defect, not a missing label.
 *
 * THE SAME KEYS THE REGISTER'S FILTER USES, deliberately. The owner picks a
 * reason here and then filters the register by it; two sets of near-identical
 * English ("Sent for job work" against "Material sent out for job work") would
 * read as two different things and send him looking for the difference.
 * ═════════════════════════════════════════════════════════════════════════════
 */
const TYPE_LABEL_KEY: Record<ChallanType, string> = {
  JOB_WORK_OUT: "challan.typeJobWorkOut",
  JOB_WORK_IN: "challan.typeJobWorkIn",
  SUPPLY_NOT_INVOICED: "challan.typeSupplyNotInvoiced",
  APPROVAL: "challan.typeApproval",
  RETURNABLE: "challan.typeReturnable",
  OTHER: "challan.typeOther",
}

/*
 * ═════════════════════════════════════════════════════════════════════════════
 * 44px ROWS, AND THIS IS NOT DECORATION.
 *
 * This field was a native `<select>` on purpose: on a phone the operating system
 * opens its own full-height wheel — bigger than anything drawn in the page,
 * already familiar, and reliably hittable without a 48px target (§11.1). The
 * owner asked for the in-page Select instead, for visual consistency with the
 * rest of the app, and that is his call. But the property the native control was
 * protecting is the one thing the swap must not lose, and shadcn's `SelectItem`
 * is about 28px (`py-1` around `text-sm`) — 16px short of a thumb with dust on
 * it. So the height is put back here, explicitly.
 *
 * ``, NEVER `min-h-[44px]`. `styles/preferences.css` scales the root font
 * size to 18px/20px for the large-text preference precisely so padding, gaps and
 * touch targets grow together; the rem class becomes 49.5px/55px for a viewer who
 * has turned it up, while a pixel literal would stay stuck at 44. It is also this
 * codebase's house spelling — see `components/ui/questionnaire.tsx`.
 *
 * UNCONDITIONAL, not behind `pointer-coarse:`. The workshop phone is the primary
 * device, a desktop mouse loses nothing from a 44px row, and an unconditional
 * class cannot be defeated by a device that misreports its pointer type.
 *
 * THE LABELS ARE ALLOWED TO WRAP. `SelectContent` is `w-(--anchor-width)` with
 * `overflow-x-hidden` and the item's text is `whitespace-nowrap`, so "Delivered,
 * bill to follow" would be half-read on a narrow phone — and a half-read reason
 * on the field that decides which party prints as CONSIGNER is a correctness
 * problem, not a cosmetic one.
 *
 * Do not "tidy" any of this away.
 * ═════════════════════════════════════════════════════════════════════════════
 */
const TOUCH_ITEM = "whitespace-normal [&>div]:whitespace-normal"
/** The trigger is now the ONLY tap target that opens the list — see `TOUCH_ITEM`. */

export function ChallanEditorScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const editing = id !== undefined && id !== ""

  const [customer, setCustomer] = useState<Customer | null>(null)
  const [pickingCustomer, setPickingCustomer] = useState(false)
  const [pickingItemFor, setPickingItemFor] = useState<string | null>(null)

  const [state, setState] = useState<ChallanEditorState>(() =>
    emptyChallanState(todayInputValue(), [emptyLine(nextKey())]),
  )

  // `id` WHEN REVISING, so the server previews the buyer the `PUT` will actually
  // keep (§4) rather than the live customer row — see `editingQuery`.
  const preview = useChallanPreview(state, editing ? id : undefined)
  const save = useSaveChallan(id)

  /*
   * TWO WAYS TO ARRIVE WITH CONTENT — one fewer than the quotation editor has:
   *   /challans/:id/edit  — revise this movement, keeping its number (§17.7)
   *   /challans/new?draft=7 — resume a form the workshop phone call interrupted
   *
   * There is deliberately no third. See the note at the top of the file on why
   * a challan is never copied.
   */
  const [searchParams] = useSearchParams()
  // TRUTHY, NOT `!== null`. A truncated link (`/challans/new?draft=`) yields ""
  // — not null — and the old quotation check treated that as a real draft id,
  // requested `/challans/drafts/`, and left the screen inert on the 404.
  const resuming = !editing && (searchParams.get("draft") ?? "") !== ""
  const source = useChallan(editing ? id : undefined)
  const [loaded, setLoaded] = useState(false)

  /*
   * ?draft=7 — resume an unfinished challan (§7.2). The saved form replaces the
   * editor's state ONCE; from then on the autosave below keeps the server copy
   * current.
   *
   * NEW CHALLANS ONLY. Revising a saved one (`/challans/:id/edit`) has no draft:
   * that document exists, holds a number out of the CHL series, and the `PUT` is
   * where its changes go. A form autosaving beside it would be a second,
   * numberless copy of a document already in the register, with no rule for
   * which wins.
   */
  const draftParam = resuming ? (searchParams.get("draft") ?? undefined) : undefined
  const resumed = useDraft<ChallanEditorState>("CHALLAN", draftParam)
  const [restored, setRestored] = useState(false)
  // A resume that FAILED (already made into a challan, or discarded on the other
  // device) must not leave autosave switched off for ever behind a blank form —
  // see the `enabled` note below.
  const resumeFailed = draftParam !== undefined && resumed.isError

  useEffect(() => {
    if (restored || resumed.data === undefined) return
    setRestored(true)
    // Hoisted so the payload stays narrowed inside the updater below.
    const saved = resumed.data.payload
    // BEFORE the payload lands in state — see `makeKeyFactory`. The restored
    // lines carry keys from a previous page load, when the counter was higher
    // than it is now; without this the next "Add line" reuses one of them, and
    // the server derives §4's SR.NO hierarchy from exactly that field.
    lineKeys.adopt(saved.lines)
    /*
     * MERGED OVER THE EMPTY FORM, NOT ASSIGNED OVER IT — a draft is JSON written
     * by WHATEVER VERSION OF THIS SCREEN saved it, and `document_drafts` has no
     * migration for its payload. A plain assignment leaves a field added later
     * `undefined`, and `text(undefined)` / `.trim()` throws on the very next
     * render: the owner opens a resumed draft to a blank crashed screen with his
     * typing still in the database. That has already happened once, on the
     * quotation editor, the day `itemNo` was added.
     *
     * It protects TOP-LEVEL keys only; `lines` is replaced wholesale, which is
     * right — a line's shape is shared and versioned with the other two editors.
     */
    /*
     * AND `adoptedLines` DOES THE SAME PER LINE, which the spread cannot: it
     * replaces `lines` wholesale, so a field added to `EditorLine` afterwards
     * arrives `undefined` on an older draft. This note used to say "no such
     * field has been added yet"; §15(3)(a)'s `discount` was the first, and it
     * threw inside `LineCard` on the very first render of a resumed draft.
     */
    setState((current) => ({ ...current, ...saved, lines: adoptedLines(saved.lines) }))
    if (saved.customerId !== null && resumed.data.customerName !== "") {
      setCustomer({
        id: saved.customerId,
        name: resumed.data.customerName,
      } as Customer)
    }
  }, [restored, resumed.data])

  const autosave = useDraftAutosave({
    // The mount, and with it the capability the API checks — `challan.create`
    // here. Never a flag on one endpoint.
    kind: "CHALLAN",
    state,
    customerName: customer?.name ?? "",
    draftId: draftParam === undefined ? null : Number(draftParam),
    initialVersion: resumed.data?.version ?? 1,
    initialStatus: resumed.data?.status ?? "DRAFT",
    // Has the resumed GET actually landed? Without this the hook cannot tell
    // "version 1, a new draft" from "version 1, the real one is still in
    // flight", and a resumed draft 409s against itself on its first autosave.
    loaded: resumed.data !== undefined,
    /*
     * NOT WHILE A RESUMED PAYLOAD IS STILL IN FLIGHT — `state` starts as an
     * empty challan, and autosaving that over a real draft before its GET lands
     * is the one way this feature could destroy typed work.
     *
     * `resumeFailed` REOPENS IT. Without that clause a resume that 404s left
     * `restored` false for ever, so autosave never switched on and every
     * keystroke after it vanished silently. There is nothing to overwrite in
     * that case — the draft does not exist — so a fresh one is the right
     * outcome, and the banner below says so.
     *
     * Off entirely while revising a saved challan — see the note on `draftParam`.
     */
    enabled: !editing && (draftParam === undefined || restored || resumeFailed),
  })

  useEffect(() => {
    // ONCE. Guarded by `loaded` rather than keyed on the query settling, so a
    // background refetch can never wipe lines he has started editing.
    const document = source.data
    if (loaded || document === undefined) return
    setLoaded(true)
    /*
     * `linesFromInvoice` IS KIND-AGNOSTIC — its parameter is structural
     * (`{ customerId, attnName, lines }`) precisely so all three document kinds
     * share one mapping rather than drifting apart. A `ChallanDetail`'s lines are
     * `DocumentLine[]`, the same array an invoice's are.
     */
    const seed = linesFromInvoice(document, nextKey)
    setState((current) =>
      stateFromChallan(document, seed.lines.length === 0 ? current.lines : seed.lines),
    )
    setCustomer(
      document.customer === null
        ? null
        : ({ id: document.customerId, name: document.customer.name } as Customer),
    )
  }, [loaded, source.data])

  /*
   * HYDRATE THE CHOSEN-CUSTOMER CARD. A loaded challan arrives with a customerId
   * and a frozen snapshot name; the card needs the whole live row (GSTIN, state,
   * the CGST-vs-IGST hint). Never overwrites a row the picker chose — the
   * condition only fires while the row in hand is partial.
   */
  const hydrate = useCustomerById(
    state.customerId !== null && (customer === null || customer.stateCode === undefined)
      ? state.customerId
      : null,
  )
  useEffect(() => {
    if (hydrate.data === undefined) return
    setCustomer(hydrate.data)
  }, [hydrate.data])

  const patch = (changes: Partial<ChallanEditorState>) => {
    setState((current) => ({ ...current, ...changes }))
  }

  const patchLine = (key: string, changes: Partial<EditorLine>) => {
    setState((current) => ({
      ...current,
      lines: current.lines.map((line) => (line.key === key ? { ...line, ...changes } : line)),
    }))
  }

  const addLine = (parentKey: string | null = null) => {
    setState((current) => ({
      ...current,
      lines: [...current.lines, { ...emptyLine(nextKey()), parentKey }],
    }))
  }

  const addGroup = () => {
    const headerKey = nextKey()
    setState((current) => ({
      ...current,
      lines: [
        ...current.lines,
        { ...emptyLine(headerKey), isGroupHeader: true },
        { ...emptyLine(nextKey()), parentKey: headerKey },
      ],
    }))
  }

  const removeLine = (key: string) => {
    setState((current) => ({
      // A header takes its children with it — §4 allows two levels, and an
      // orphaned child would be a measured line with no heading above it.
      ...current,
      lines: current.lines.filter((line) => line.key !== key && line.parentKey !== key),
    }))
  }

  const body = toChallanApiBody(state)
  const canSave = body !== null && preview.data !== undefined && !save.isPending

  /*
   * WHICH WAY THE GOODS GO — `null` until the type is answered.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * DERIVED THROUGH THE SHARED `challanDirection`, WHICH IS NOT A SECOND COPY OF
   * THE MAPPING — it IS the mapping, the same function the server calls to fill
   * `ChallanResponse.direction` and to decide which address block prints as
   * consigner. The rule elsewhere ("never re-derive direction on a screen") is
   * about the DETAIL screen, which holds a server response and must show the
   * field on it. This screen is authoring a document that does not exist yet;
   * there is no response to read, and the only honest alternative to calling the
   * shared function would be an `if` written out here — which is exactly the
   * second copy that could disagree.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const direction = state.challanType === null ? null : challanDirection(state.challanType)
  const inward = direction === "INWARD"

  /*
   * PREVIEW — the unsaved form as the PAPER THAT GOES IN THE LORRY, DRAFT-stamped
   * corner to corner.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * THIS IS THE SCREEN WHERE PREVIEW EARNS ITS PLACE, AND THE CONSIGNER BLOCK IS
   * WHY. The two address blocks Rule 55(1) requires are the one part of this
   * document that is INVISIBLE until it is printed: on `JOB_WORK_IN` the CUSTOMER
   * prints as the consigner, and although the note beside the reason field says
   * the direction in words, nothing on this screen shows the party blocks
   * themselves. Naming the receiver as the sender of goods already on a lorry is a
   * false document, not a layout slip — and the person who finds out is the driver
   * at somebody's gate.
   *
   * NOTHING HERE DECIDES THE SWAP. The server calls the same shared
   * `challanDirection` this screen calls, and `challanParties` reads it; the
   * preview simply shows the result.
   *
   * DISABLED, NOT HIDDEN, UNTIL THE FORM COULD BE SAVED — and that costs no code:
   * `toChallanApiBody` returns `null` while the reason is unanswered, which is the
   * SAME `canSave` that disables Save beside it. The reason is already said in
   * words twice on this screen (the totals bar prints `line.totalIncomplete`, and
   * the reason field's own help says the direction is unknown), so a third,
   * preview-specific message would be one fact worded three ways. The owner's
   * "hide, do not disable" rule is about controls that do not APPLY to a document;
   * Preview applies to every challan and is momentarily not YET usable, exactly
   * like Save.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const [previewing, setPreviewing] = useState(false)
  const onPreviewPdf = async () => {
    if (body === null) return
    setPreviewing(true)
    try {
      /*
       * TWO IDS, AND THEY MEAN DIFFERENT THINGS. `draft` is the saved FORM's id,
       * so the number box prints DRAFT/CHAL/7 — never a predicted CHL number,
       * which is allocated inside the save (§6, §17.4). `editing` is the saved
       * CHALLAN this form would replace, which is what makes the sheet print the
       * buyer the `PUT` keeps (§4) — and therefore the place of supply — instead
       * of the live customer row.
       */
      const params = new URLSearchParams()
      if (autosave.draftUsedId !== null) params.set("draft", String(autosave.draftUsedId))
      if (editing && id !== undefined) params.set("editing", id)
      const suffix = params.size === 0 ? "" : `?${params.toString()}`
      const fetched = await requestBlob(`${endpoints.challanPreviewPdf}${suffix}`, {
        body,
        fallbackFilename: "draft-preview.pdf",
      })
      const url = URL.createObjectURL(fetched.blob)
      // A NEW TAB: the half-typed form must survive the look.
      window.open(url, "_blank", "noopener")
      window.setTimeout(() => {
        URL.revokeObjectURL(url)
      }, 60_000)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("err.somethingWentWrong"))
    } finally {
      setPreviewing(false)
    }
  }

  const onSave = () => {
    if (body === null) return
    save.mutate(body, {
      onSuccess: (result) => {
        toast.success(t("status.saved"))
        /*
         * The challan exists with a number now, so the form has done its job.
         * Leaving it would show a second, numberless copy of a document already
         * in the register — and the owner would have no way to tell which of the
         * two the lorry is carrying.
         */
        void autosave.completed()
        /*
         * THE WARNINGS ARE NOT THROWN AWAY. The server reports what it DECIDED
         * differently — a rate overruled by the versioned master (§8.7), an
         * archived customer. `duration: Infinity` because a 4-second auto-hide
         * is how a warning gets technically shown and never read.
         */
        for (const warning of result.warnings ?? []) {
          toast.warning(warning, { duration: Infinity, closeButton: true })
        }
        void navigate(routes.challan(result.data.id), { replace: true })
      },
    })
  }

  // An edit whose document is still on its way: the form must not render empty
  // and invite typing into a state that is about to be replaced.
  if (editing && source.isPending) return <EditorSkeleton />

  if (editing && source.isError) {
    /*
     * A 404 HERE IS AMBIGUOUS BY DESIGN and this screen must not resolve it into
     * words it cannot justify. Every challan route sits behind
     * `requireFlag("challans")`, which runs before `requireCap` and answers 404
     * rather than 403 so "forbidden" cannot leak that unreleased work exists
     * (§18.3) — so this means either "no such challan" or "the feature is off".
     * `err.notFound` is true of both; "you do not have permission" is the very
     * leak the 404 was chosen to avoid.
     */
    const notFound = source.error instanceof ApiError && source.error.status === 404
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <BackLink />
        <Alert variant="destructive">
          <AlertTitle>{notFound ? t("err.notFound") : t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription>
            {source.error instanceof ApiError
              ? source.error.message
              : t("err.somethingWentWrong")}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  /*
   * THE SERVER'S RULE, RE-READ AT RENDER, AND BEFORE ANY RETYPING. `isEditable`
   * is OPEN-only and the `PUT` asserts it again, so this is a courtesy rather
   * than the gate — but without it a link followed from a stale register would
   * present a full editor over a movement that is finished and then reject the
   * save at the very end, after the whole sheet had been redone.
   *
   * THE SERVER'S FIELD, NOT `isChallanEditable(status)`. Both exist and agree
   * today; the response is the one that cannot fall behind a rule change.
   *
   * NO "MAKE A COPY" BUTTON BESIDE "VIEW", unlike the quotation's version of this
   * alert. There is no copy route and there will not be one — a new movement is
   * a new challan, typed fresh, with today's date and today's lorry.
   */
  if (editing && source.data !== undefined && !source.data.isEditable) {
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <BackLink />
        <Alert>
          <AlertTitle>{t("challan.lockedTitle")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>{t("challan.lockedHelp")}</span>
            <Button
              variant="outline"
              nativeButton={false}
              render={<Link to={routes.challan(source.data.id)} />}
            >
              {t("btn.viewChallan")}
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 pb-32 md:gap-6 md:p-6 md:pb-32">
      <BackLink />

      {/* The same header grammar as the challan screen it becomes: identity
        * left, the draft's name where a status badge sits elsewhere, so dad
        * learns the layout once.
        *
        * NO STATUS BADGE. `DocumentStatusBadge` knows `invoice` and `quotation`
        * only, and its table has no OPEN / RETURNED / INVOICED — passing
        * `kind="challan"` is a type error and passing no kind prints the raw
        * SCREAMING_SNAKE value in front of the owner, which is the defect that
        * component exists to prevent. It would also say nothing: a challan this
        * screen will open at all is OPEN by definition, because the branch above
        * refuses every other status. */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight tabular-nums">
              {editing && source.data !== undefined
                ? t("challan.editTitle", { number: source.data.number })
                : t("challan.newTitle")}
            </h1>
            {/* The saved form's own name. It never appears while editing, because
              * that document already holds a number. */}
            {!editing && autosave.draftUsedId !== null ? (
              <DraftNameBadge kind="CHALLAN" id={autosave.draftUsedId} status={autosave.status} />
            ) : null}
          </div>
          <p className="text-muted-foreground text-sm">
            {/* §6 — a new challan's number comes out of the CHL series inside the
              * save transaction so the series has no gaps, and challan serial
              * ranges are declared in GSTR-1 (§17.4). There is nothing honest to
              * show yet. An edit keeps the number the driver is carrying. */}
            {editing ? t("challan.editSubtitle") : t("challan.numberOnSave")}
          </p>
        </div>

        {/*
          * THE SAME CONTROLS, IN THE SAME PLACE, AS THE OTHER TWO EDITORS (§7.2)
          * — the autosave clock, "Save now", and the READY handoff. Dad
          * learns this row once and it means the same thing on all three screens.
          *
          * Absent while REVISING a saved challan: that document is already stored
          * under its own number and the save button at the bottom writes to it.
          */}
        {editing ? null : (
          <div className="flex flex-wrap items-center gap-2" role="status">
            {autosave.saving ? (
              <Badge
                variant="outline"
                className="h-8 gap-1.5 rounded-lg px-2.5 text-sm font-medium [&>svg]:size-4!"
              >
                <Spinner data-icon="inline-start" />
                {t("draft.saving")}
              </Badge>
            ) : (
              <span className="text-muted-foreground text-xs">
                {autosave.savedAt !== null
                  ? t("draft.savedAt", {
                      time: autosave.savedAt.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      }),
                    })
                  : t("draft.notYet")}
              </span>
            )}
            <Button
              type="button"
              variant="outline"
              disabled={!autosave.canSaveNow || autosave.saving}
              onClick={autosave.saveNow}
            >
              <SaveIcon />
              {t("btn.saveNow")}
            </Button>
            <DraftReadyControl
              status={autosave.status}
              exists={autosave.draftUsedId !== null}
              pending={autosave.settingStatus}
              onChange={autosave.changeStatus}
            />
          </div>
        )}
      </header>

      {/* The other device won a concurrent edit of this form (§7.2). Autosave
        * has stopped; reloading adopts the newer version. */}
      {autosave.conflict !== null ? <DraftConflictAlert kind="CHALLAN" /> : null}

      {/*
        * THE RESUME FAILED, AND SAYING SO BEATS A BLANK FORM. The draft was
        * turned into a document or discarded elsewhere; autosave has been
        * reopened (see `enabled`) so typing from here starts a fresh draft rather
        * than disappearing.
        */}
      {resumeFailed ? (
        <Alert>
          <AlertTitle>{t("draft.goneTitle")}</AlertTitle>
          <AlertDescription>{t("draft.goneHelp")}</AlertDescription>
        </Alert>
      ) : null}

      {save.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotSave")}</AlertTitle>
          <AlertDescription>
            {save.error instanceof ApiError ? save.error.message : t("err.somethingWentWrong")}
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t("challan.movementTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {/*
            * ═══════════════════════════════════════════════════════════════════
            * THE FIRST FIELD ON THE FORM, AND THE MOST CONSEQUENTIAL ONE.
            *
            * It is the legal basis for the movement under Rule 55(1), and
            * `challanDirection` reads it to decide which party prints as the
            * CONSIGNER. A wrong answer does not produce an untidy document — it
            * produces a FALSE one, naming the receiver as the sender of goods
            * already on a lorry. So it is asked first, before the customer and
            * before a single line, and it starts UNANSWERED: `ChallanCreateSchema`
            * gives it no default for exactly this reason, and a preselected
            * "goods sent for job work" would be answered-looking and silently
            * wrong on every challan recording material coming IN.
            *
            * THE EMPTY OPTION STAYS SELECTABLE rather than being a `disabled`
            * placeholder. A greyed control is the thing the owner has objected to
            * before, and "I do not yet know why these are moving" is a real state
            * of a half-filled form: returning the field to it leaves the totals
            * bar saying the challan is incomplete, which is true, instead of
            * trapping a wrong first guess on the page.
            *
            * WHICH IS WHY THE EMPTY ITEM AND THE PLACEHOLDER CARRY THE SAME KEY.
            * Base UI reads `value=""` as NO VALUE (`SelectRoot`: `hasSelectedValue
            * = value != null && serializedValue !== ''`), so the trigger renders
            * the PLACEHOLDER rather than the empty item's own text. Giving both
            * `challan.typeUnchosen` makes the trigger read identically whichever
            * branch Base UI takes — the sentence the native `<select>` showed —
            * while `onValueChange` still fires with `""` when the row is tapped,
            * so the field remains genuinely re-answerable. Do not "simplify" this
            * into a `disabled` placeholder item: that removes the way back.
            * ═══════════════════════════════════════════════════════════════════
            */}
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            {/* The REGISTER'S OWN LABEL for this field, not a second wording of
              * it. He answers it here and then filters the book by it. */}
            <Label htmlFor="challan-type">{t("challan.typeLabel")}</Label>
            {/* THE TYPE ARGUMENT IS LOAD-BEARING. Base UI infers the value type
              * from the `value` prop, and `state.challanType ?? ""` hands it a
              * fresh `""` literal, which TypeScript widens — collapsing the whole
              * union to `string` and quietly re-admitting "any string is a
              * challan type". Naming it keeps `withChallanType` type-checked
              * against the enum, which is what stops a typo becoming a movement
              * with no stated reason under Rule 55(1). */}
            <Select<ChallanType | "">
              value={state.challanType ?? ""}
              /*
               * NEVER THE ENUM, ON THE TRIGGER EITHER. Without this Base UI's
               * `Select.Value` prints the raw value — `JOB_WORK_IN` — because it
               * has no way to know what the item rendered. `TYPE_LABEL_KEY` is the
               * same table the list below reads, so the closed control and the
               * open one cannot say different things. The `""` guard is not
               * defensive noise: closed-trigger typeahead calls this with every
               * candidate value, including the empty one.
               */
              itemToStringLabel={(type) =>
                type === "" ? t("challan.typeUnchosen") : t(TYPE_LABEL_KEY[type])
              }
              onValueChange={(chosen) => {
                /*
                 * THROUGH `withChallanType`, NEVER A PLAIN `patch`. It re-seeds
                 * `isReturnable` from `defaultIsReturnable(type)` and clears the
                 * return date when the new type is one-way — and the pair
                 * `isReturnable: false` + `expectedReturnDate` is a 400 that
                 * breaks the DEBOUNCED PREVIEW as well as the save, parking the
                 * totals bar in an error state with no visible cause.
                 *
                 * `null` IS FOLDED IN WITH `""`. `onValueChange` is typed to allow
                 * it, and "no reason given" is the one honest reading of both.
                 */
                setState((current) =>
                  chosen === "" || chosen === null
                    ? { ...current, challanType: null }
                    : withChallanType(current, chosen),
                )
              }}
            >
              <SelectTrigger id="challan-type" className="w-full">
                <SelectValue placeholder={t("challan.typeUnchosen")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem className={TOUCH_ITEM} value="">
                  {t("challan.typeUnchosen")}
                </SelectItem>
                {/* Iterated from the enum, not listed by hand, so a new type
                  * cannot be added to the database and forgotten here. */}
                {CHALLAN_TYPES.map((type) => (
                  <SelectItem className={TOUCH_ITEM} key={type} value={type}>
                    {t(TYPE_LABEL_KEY[type])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">{t("challan.typeHelp")}</p>
          </div>

          {/*
            * WHICH WAY THE GOODS ARE GOING, SAID OUT LOUD, THE MOMENT IT IS
            * DECIDED. Rule 55(1) requires both parties named on the document, and
            * the direction is what decides which of them is which. The owner
            * should not have to know that "customer material received" implies he
            * is the consignee — the screen says it, in the same breath as the
            * choice that caused it, while it can still be corrected.
            */}
          <div className="sm:col-span-2">
            {direction === null ? (
              <p className="text-muted-foreground rounded-md border border-dashed p-3 text-xs">
                {t("challan.directionUnknown")}
              </p>
            ) : (
              <div className="flex items-start gap-3 rounded-md border p-3">
                {inward ? (
                  <ArrowDownLeftIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                ) : (
                  <ArrowUpRightIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                )}
                <div className="flex flex-col gap-0.5">
                  {/* "Received in" / "Sent out" — the SAME two words the register
                    * badges a challan with, so the row he sees tomorrow says what
                    * this box said today. */}
                  <span className="text-sm font-medium">
                    {inward ? t("challan.directionInward") : t("challan.directionOutward")}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {inward ? t("challan.directionInwardHelp") : t("challan.directionOutwardHelp")}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/*
            * "CUSTOMER" IS THE WRONG WORD ON AN INWARD CHALLAN, and this is the
            * one field where that matters: the row chosen here becomes the
            * CONSIGNER when the customer's own material is coming in, and the
            * CONSIGNEE on every other type. The word "customer" is kept in both
            * labels because that is what the owner calls the row and what the
            * picker searches; the bracket says which side of the movement they
            * are on today.
            */}
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>
              {direction === null
                ? t("lbl.customer")
                : inward
                  ? t("challan.partyInwardLabel")
                  : t("challan.partyOutwardLabel")}
            </Label>
            <ChosenCustomer
              customer={customer}
              onChange={() => {
                setPickingCustomer(true)
              }}
            />
            {direction === null ? null : (
              <p className="text-muted-foreground text-xs">
                {inward ? t("challan.partyInwardHelp") : t("challan.partyOutwardHelp")}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="challan-date">{t("lbl.date")}</Label>
            <DateField
              id="challan-date"
              value={state.date}
              onChange={(value) => {
                patch({ date: value })
              }}
            />
            {/* §8.7 — the date selects the GST rate period, and §6's challan
              * series resets on it. Not cosmetic. */}
            <p className="text-muted-foreground text-xs">{t("invoice.dateHelp")}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="challan-attn">{t("lbl.contactName")}</Label>
            <Input
              id="challan-attn"
              value={state.attnName}
              onChange={(event) => {
                patch({ attnName: event.target.value })
              }}
              maxLength={191}
            />
            <p className="text-muted-foreground text-xs">{t("challan.attnHelp")}</p>
          </div>

          {/*
            * ARE THEY COMING BACK? Seeded from the type by `withChallanType`
            * (only `SUPPLY_NOT_INVOICED` is a one-way trip) and then whatever the
            * owner says — the galvaniser sometimes keeps the offcuts, and he
            * should not have to argue with the form about it.
            *
            * UNTICKING CLEARS THE DATE, through `withIsReturnable`. The API
            * refuses `isReturnable: false` sent with an `expectedReturnDate`, and
            * because one body feeds both the debounced preview and the save, an
            * inconsistent pair does not merely fail on Save — it makes every
            * preview 400 for the rest of the session.
            */}
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <div className="flex items-center gap-3">
              <Switch
                id="challan-returnable"
                checked={state.isReturnable}
                onCheckedChange={(checked) => {
                  setState((current) => withIsReturnable(current, checked))
                }}
              />
              <Label htmlFor="challan-returnable">{t("challan.returnableLabel")}</Label>
            </div>
            <p className="text-muted-foreground text-xs">{t("challan.returnableHelp")}</p>
          </div>

          {/*
            * HIDDEN, NOT DISABLED, when nothing is coming back. A greyed date box
            * invites hunting for the way to un-grey it, and the answer would be
            * the tick box directly above — which is already the control that
            * governs it. It reappears empty, because `withIsReturnable` cleared
            * the value rather than merely hiding it.
            */}
          {state.isReturnable ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="challan-return-by">{t("challan.expectedReturnLabel")}</Label>
              <DateField
                id="challan-return-by"
                value={state.expectedReturnDate}
                placeholder={t("challan.expectedReturnPlaceholder")}
                onChange={(value) => {
                  patch({ expectedReturnDate: value })
                }}
                clearable
              />
              <p className="text-muted-foreground text-xs">{t("challan.expectedReturnHelp")}</p>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="challan-vehicle">{t("lbl.vehicleNumber")}</Label>
            <Input
              id="challan-vehicle"
              value={state.vehicleNumber}
              onChange={(event) => {
                // AS TYPED. No uppercasing, no stripping of spaces or hyphens —
                // this is what the owner read off the lorry, and the person at
                // the gate is comparing it against the same plate.
                patch({ vehicleNumber: event.target.value })
              }}
              // `optionalText(20)` on the schema; stopping the typing beats a 400
              // after the whole challan is filled in.
              maxLength={20}
              spellCheck={false}
            />
            <p className="text-muted-foreground text-xs">{t("challan.vehicleHelp")}</p>
          </div>

          {/*
            * BLANK IS THE ORDINARY CASE and it means "the address printed above".
            * Nothing is synthesised into it — an invented placeholder address on
            * a document that travels with goods is a false statement about where
            * they went.
            */}
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="challan-ship-to">{t("lbl.shipToAddress")}</Label>
            <Textarea
              id="challan-ship-to"
              rows={3}
              value={state.shipToAddress}
              onChange={(event) => {
                patch({ shipToAddress: event.target.value })
              }}
              maxLength={1000}
            />
            <p className="text-muted-foreground text-xs">{t("challan.shipToHelp")}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("line.heading")}</CardTitle>
          <CardDescription>{t("line.serverRecomputes")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {/*
            * SAID BEFORE THE FIGURES, NOT AFTER. The shared totals bar at the
            * bottom of this screen prints a rupee amount with CGST and SGST
            * beside it, because Rule 55(1) requires a taxable value on a challan
            * — and an unexplained rupee amount on a document reads as money owed.
            * It is not: nothing on a challan is output tax, none of it reaches
            * GSTR-3B, and the bill for this work is a separate document.
            */}
          <p className="text-muted-foreground rounded-md border border-dashed p-3 text-xs">
            {t("challan.notATaxInvoice")}
          </p>

          {state.lines.map((line) => (
            <LineCard
              key={line.key}
              line={line}
              lines={state.lines}
              preview={preview.data?.data.lines.find((row) => row.key === line.key)}
              /*
               * NO DISCOUNT BOX. A challan declares the value of the goods
               * MOVING (Rule 55), not a price being negotiated, so §15(3)(a) is
               * out of scope and `ChallanCreateSchema.lines` carries no
               * `discountPaise` — `z.object()` strips it. The box was on this
               * screen and did nothing: it took a number, the debounced preview
               * came back with the taxable value unchanged, no error appeared,
               * and the challan that travelled with the lorry declared the full
               * amount. Removing it is the honest half of that scope decision.
               */
              discountable={false}
              onChange={(changes) => {
                patchLine(line.key, changes)
              }}
              onRemove={() => {
                removeLine(line.key)
              }}
              onPickItem={() => {
                setPickingItemFor(line.key)
              }}
              onAddChild={() => {
                addLine(line.key)
              }}
            />
          ))}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                addLine()
              }}
            >
              <PlusIcon />
              {t("btn.addLine")}
            </Button>
            <Button type="button" variant="outline" onClick={addGroup}>
              <ListPlusIcon />
              {t("btn.addSubLine")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("challan.wordsTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="challan-notes">{t("lbl.notes")}</Label>
            <Textarea
              id="challan-notes"
              rows={3}
              value={state.notes}
              onChange={(event) => {
                patch({ notes: event.target.value })
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="challan-terms">{t("lbl.terms")}</Label>
            <Textarea
              id="challan-terms"
              rows={3}
              value={state.terms}
              onChange={(event) => {
                patch({ terms: event.target.value })
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/*
        * `onPreviewPdf` IS WHAT SHOWS THE PREVIEW BUTTON — see `onPreviewPdf`
        * above for why this screen wants it most of the three. This block used to
        * say the route did not exist; `POST /challans/preview-pdf` landed in
        * `apps/api/src/routes/documents-pdf.ts` (not in `routes/challans.ts`,
        * which is why nobody noticed the claim had gone stale).
        *
        * NO `emphasiseSubtotal`: that is §9.3's quotation-only rule about which
        * figure a quotation SHEET prints as its headline. It has no meaning on a
        * challan.
        */}
      <TotalsBar
        preview={preview}
        canSave={canSave}
        saving={save.isPending}
        incomplete={body === null}
        onSave={onSave}
        previewing={previewing}
        onPreviewPdf={() => {
          void onPreviewPdf()
        }}
        saveLabel={editing ? t("btn.saveChanges") : t("btn.saveChallan")}
      />

      <CustomerPicker
        open={pickingCustomer}
        onOpenChange={setPickingCustomer}
        onPick={(picked) => {
          setCustomer(picked)
          patch({
            customerId: picked.id,
            // Only when the field is still empty — never over something typed.
            attnName: state.attnName === "" ? (picked.defaultContactName ?? "") : state.attnName,
          })
        }}
      />

      <ItemPicker
        open={pickingItemFor !== null}
        onOpenChange={(open) => {
          if (!open) setPickingItemFor(null)
        }}
        onPick={(item) => {
          if (pickingItemFor === null) return
          // The saved job fills the line, INCLUDING its HSN and GST rate — Rule
          // 55(1) requires the HSN and the description on a challan just as §4.1
          // requires them on an invoice, and those are the two fields most
          // easily got wrong by hand.
          patchLine(pickingItemFor, {
            itemId: item.id,
            description: item.description ?? item.name,
            supplyNature: item.supplyNature as EditorLine["supplyNature"],
            hsn: item.hsn,
            gstRatePercent: basisPointsToInputValue(item.gstRateBp),
            pricingMode: item.pricingMode as EditorLine["pricingMode"],
            unitLabel: item.unitLabel,
            rate: item.ratePaise === 0 ? "" : (item.ratePaise / 100).toFixed(2),
          })
          setPickingItemFor(null)
        }}
      />
    </div>
  )
}

function BackLink(): React.JSX.Element {
  const { t } = useTranslation()
  return (
    <Link
      to={routes.challans}
      className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
    >
      <ArrowLeftIcon className="size-4" />
      {t("nav.challans")}
    </Link>
  )
}

function EditorSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}
